import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import logo from "../assets/Nikao_Pulse_Asset_Register_Logo.png";
import {
  Dashboard as DashboardIcon,
  List as SupplierListIcon,
  Article as LicenseIcon,
  Category as CategoryIcon,
  Assessment as ReportsIcon,
  Security as AuditIcon,
  People as UsersIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Logout,
  Settings,
  Schedule as ScheduleIcon,
  VpnKey as CertsSecretsIcon,
  Computer as AssetRegisterIcon,
  VerifiedUser as AttestationIcon,
  Apps as SoftwarePermissionsIcon,
  Business as ClientsIcon,
  Psychology as AiSystemsIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import {
  getUserDisplayName,
  getUserInitials,
  getUserRoleLabel,
} from "../utils/userDisplay";
import { formatAppVersionLabel } from "../utils/appVersion";
import { isAdminOrAbove, isSuperAdmin } from "../utils/roles";
import SettingsModal from "./SettingsModal";
import { NIKAO_BRAND } from "../constants/nikaoBrand";

const drawerWidth = 240;

const getMenuItems = (userRole) => [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
  {
    text: "Supplier Register",
    icon: <SupplierListIcon />,
    path: "/supplier-lists",
  },
  { text: "Clients Register", icon: <ClientsIcon />, path: "/clients" },
  { text: "Business Services", icon: <LicenseIcon />, path: "/licenses" },
  {
    text: "Certs & Secrets",
    icon: <CertsSecretsIcon />,
    path: "/certs-secrets",
  },
  {
    text: "Asset Register",
    icon: <AssetRegisterIcon />,
    path: "/asset-register",
  },
  {
    text: "AI Systems Register",
    icon: <AiSystemsIcon />,
    path: "/ai-systems",
  },
  {
    text: "Software & Permissions",
    icon: <SoftwarePermissionsIcon />,
    path: "/software-permissions",
  },
  { text: "Attestation", icon: <AttestationIcon />, path: "/attestation" },
  { text: "Reports", icon: <ReportsIcon />, path: "/reports" },
  { text: "Audit Log", icon: <AuditIcon />, path: "/audit" },
  {
    text: "Notifications",
    icon: <NotificationsIcon />,
    path: "/notification-preferences",
  },
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    handleMenuClose();
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const drawer = (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      bgcolor={NIKAO_BRAND.white}
    >
      <Toolbar
        sx={{
          px: 0.5,
          py: 0.75,
          minHeight: "auto !important",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Pulse Asset Register - Powered by Nikao"
          sx={{
            width: "100%",
            maxWidth: "232px",
            height: "auto",
            display: "block",
          }}
        />
      </Toolbar>
      <Divider sx={{ borderColor: "rgba(22, 14, 43, 0.08)" }} />
      <List sx={{ flex: 1, overflow: "auto", py: 1 }}>
        {getMenuItems(user?.role).map((item) => {
          const selected = location.pathname === item.path;
          return (
            <ListItem
              component="button"
              key={item.text}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              selected={selected}
              sx={{
                width: "100%",
                textAlign: "left",
                border: "none",
                cursor: "pointer",
                mx: 1,
                my: 0.25,
                borderRadius: 1,
                color: NIKAO_BRAND.aubergine,
                backgroundColor: "transparent",
                borderLeft: "3px solid transparent",
                "& .MuiListItemIcon-root": {
                  color: selected ? NIKAO_BRAND.ocean : "rgba(22, 14, 43, 0.55)",
                  minWidth: 40,
                },
                "&:hover": {
                  backgroundColor: "rgba(0, 119, 182, 0.06)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(0, 119, 182, 0.12)",
                  borderLeftColor: NIKAO_BRAND.ocean,
                  "& .MuiListItemText-primary": {
                    fontWeight: 600,
                    color: NIKAO_BRAND.aubergine,
                  },
                  "&:hover": {
                    backgroundColor: "rgba(0, 119, 182, 0.16)",
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: "rgba(22, 14, 43, 0.08)" }} />
      <Box px={2} py={1.5} alignSelf="stretch" textAlign="left">
        <Typography variant="body2" color="text.secondary" noWrap>
          {getUserDisplayName(user)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {formatAppVersionLabel()}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        color="transparent"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: NIKAO_BRAND.aubergine,
          color: NIKAO_BRAND.white,
          boxShadow: "none",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {getMenuItems(user?.role).find(
              (item) => item.path === location.pathname,
            )?.text ||
              ({
                "/categories": "Categories",
                "/users": "User Management",
                "/cron-schedules": "Cron Schedules",
              }[location.pathname] ??
                "Dashboard")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              size="large"
              aria-label="manage categories"
              onClick={() => navigate("/categories")}
              color="inherit"
              sx={{ mr: 1 }}
            >
              <CategoryIcon />
            </IconButton>
            {isAdminOrAbove(user?.role) && (
              <IconButton
                size="large"
                aria-label="settings"
                onClick={handleSettingsOpen}
                color="inherit"
                sx={{ mr: 1 }}
              >
                <Settings />
              </IconButton>
            )}
            {isSuperAdmin(user?.role) && (
              <IconButton
                size="large"
                aria-label="cron schedules"
                onClick={() => navigate("/cron-schedules")}
                color="inherit"
                sx={{ mr: 1 }}
              >
                <ScheduleIcon />
              </IconButton>
            )}
            {isAdminOrAbove(user?.role) && (
              <IconButton
                size="large"
                aria-label="manage users"
                onClick={() => navigate("/users")}
                color="inherit"
                sx={{ mr: 1 }}
              >
                <UsersIcon />
              </IconButton>
            )}
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar
                width={32}
                height={32}
                fontSize="0.875rem"
                sx={{
                  bgcolor: NIKAO_BRAND.teal,
                  color: NIKAO_BRAND.aubergine,
                  fontWeight: 700,
                }}
              >
                {getUserInitials(user)}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <Box px={2} py={1.5} minWidth={240}>
                <Typography fontWeight={600} noWrap>
                  {getUserDisplayName(user)}
                </Typography>
                {user?.email ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    mt={0.25}
                  >
                    {user.email}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {getUserRoleLabel(user?.role)}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "1px solid rgba(22, 14, 43, 0.08)",
              backgroundColor: NIKAO_BRAND.white,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "1px solid rgba(22, 14, 43, 0.08)",
              backgroundColor: NIKAO_BRAND.white,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        <Outlet />
      </Box>
      {isAdminOrAbove(user?.role) && (
        <SettingsModal open={settingsOpen} onClose={handleSettingsClose} />
      )}
    </Box>
  );
};

export default Layout;
